import { Icon, ImgIcons, Tooltip, useThemePart } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { TabsBarSC as T } from './TabsBarSC';

export interface TabsProps {
    title: string;
    active: boolean;
    icon?: ImgIcons;
    hidden?: boolean;
    checked?: boolean;
    msg?: string;
}

type TabsBarProps = {
    input: TabsProps[];
    onChange?: (idx: number) => void;
    additionalContent?: JSX.Element | JSX.Element[];
    verticalSlider?: boolean;
    activeBold?: boolean;
    shadowed?: boolean;
    scrollable?: boolean;
    isSmallDevice?: boolean;
} & (
        | { autoMode: boolean; infoMode?: "hover" | "tooltip"; iconOnly?: never }
        | { autoMode?: never } & ({ iconOnly: boolean; infoMode?: "hover" | "tooltip" } | { iconOnly?: undefined; infoMode?: never })
    );

const TabsBar = (p: TabsBarProps) => {
    const { input, onChange, additionalContent, verticalSlider, activeBold, shadowed, scrollable, iconOnly, infoMode, autoMode, isSmallDevice } = p;
    const [tabs, setTabs] = React.useState<TabsProps[]>();
    const [actTab, setActTab] = React.useState<number>(-1);
    const [autoIconOnlyCount, setAutoIconOnlyCount] = React.useState(0);
    const tabsContainerRef = React.useRef<HTMLDivElement>(null);
    const contentWidth = useThemePart(x => x.content.width);
    const tabIconColor = useThemePart(x => x.tabsBar?.fontColor ?? "@lightGrey");
    const tabIconColorActive = useThemePart(x => x.tabsBar?.fontColorActive ?? "@darkerGrey");
    const prevContentWidthRef = React.useRef(contentWidth);
    const prevTabsLengthRef = React.useRef(0);

    React.useEffect(() => {
        setTabs(input);
    }, [input]);

    const changeTab = React.useMemo(() => (tab: TabsProps) => {
        const copy = tabs ? _.clone(tabs) : [];
        for (let i = 0; i < copy.length; i++) {
            const c = copy[i];
            if (c.active) {
                setActTab(i);
            }
            if (c === tab) {
                c.active = true;
                if (onChange)
                    onChange(i);
            }
            else c.active = false;
        }
        setTabs(copy);
    }, [tabs, onChange]);

    const filteredTabs = React.useMemo(() => _.filter(tabs, f => f.hidden !== true), [tabs]);

    React.useLayoutEffect(() => {
        if (!autoMode) return;

        const contentChanged = contentWidth !== prevContentWidthRef.current;
        const tabsChanged = filteredTabs.length !== prevTabsLengthRef.current;
        prevContentWidthRef.current = contentWidth;
        prevTabsLengthRef.current = filteredTabs.length;

        if ((contentChanged || tabsChanged) && autoIconOnlyCount !== 0) {
            setAutoIconOnlyCount(0);
            return;
        }

        const container = tabsContainerRef.current;
        if (!container) return;

        const containerWidth = container.getBoundingClientRect().width;
        let totalTabWidth = 0;
        for (let i = 0; i < container.children.length; i++) {
            totalTabWidth += container.children[i].getBoundingClientRect().width;
        }
        if (totalTabWidth > containerWidth) {
            setAutoIconOnlyCount(c => Math.min(c + 1, filteredTabs.length));
        }
    }, [autoMode, contentWidth, filteredTabs.length, autoIconOnlyCount, actTab]);

    return (
        <T.Container
            tabsLength={filteredTabs.length}
            shadowed={shadowed}
            style={{
                gridTemplateColumns: isSmallDevice === true ? "1fr" : (autoMode ? "1fr max-content" : undefined),
                overflowX: scrollable ? "auto" : undefined,
                overflowY: scrollable ? "hidden" : undefined
            }}
        >
            {autoMode ? (
                <>
                    <T.TabsContainer ref={tabsContainerRef} style={{ gridRowStart: isSmallDevice === true ? 2 : 1 }}>
                        {_.map(filteredTabs, (t, i) => {
                            const direction = verticalSlider ? "bottom" : i < actTab ? "left" : "right";
                            const effectiveIconOnly = i >= filteredTabs.length - autoIconOnlyCount;
                            const allIconOnly = autoIconOnlyCount >= filteredTabs.length;
                            return (
                                <T.Tab key={i} active={t.active} onClick={() => changeTab(t)} checked={t.checked} activeBold={activeBold} shadowed={shadowed}>
                                    {effectiveIconOnly && (!t.active || allIconOnly) && t.icon
                                        ? (() => {
                                            const iconColor = t.checked === true ? "@accentGreen" : t.msg ? "@accentRed" : tabIconColor;
                                            return infoMode !== "hover"
                                                ? <Tooltip noMargin tooltipText={t.title} position='top'>
                                                    <Icon name={t.icon} color={iconColor} style={{ float: "left", marginRight: 0 }} />
                                                </Tooltip>
                                                : <>
                                                    <Icon name={t.icon} color={iconColor} style={{ float: "left", marginRight: 10 }} />
                                                    <span className="icon-only-title">{t.title}</span>
                                                </>;
                                        })()
                                        : <>
                                            {t.icon && <Icon name={t.icon} color={t.active ? tabIconColorActive : tabIconColor} style={{ float: "left", marginRight: 10 }} />}
                                            {t.title}
                                        </>
                                    }
                                    {t.active && <T.Underline hasIcon={t.icon !== undefined} direction={direction} checked={t.checked} hasMsg={!!t.msg} />}
                                    {(t.checked === true && !effectiveIconOnly) && <Icon name={"check"} color={"@accentGreen"} style={{ float: "right", marginLeft: 10 }} />}
                                    {(t.msg && !effectiveIconOnly) && <Tooltip noMargin tooltipText={t.msg} position='top'>
                                        <Icon name={"asterisk"} color={"@accentRed"} style={{ float: "right", marginLeft: 10 }} />
                                    </Tooltip>}
                                </T.Tab>
                            );
                        })}
                    </T.TabsContainer>
                    <T.Rest>{additionalContent}</T.Rest>
                </>
            ) : (
                <>
                    {_.map(filteredTabs, (t, i) => {
                        const direction = verticalSlider ? "bottom" : i < actTab ? "left" : "right";
                        return (
                            <T.Tab key={i} active={t.active} onClick={() => changeTab(t)} checked={t.checked} activeBold={activeBold} shadowed={shadowed}>
                                {iconOnly && !t.active && t.icon
                                    ? infoMode !== "hover"
                                        ? <Tooltip noMargin tooltipText={t.title} position='top'>
                                            <Icon name={t.icon} style={{ float: "left", marginRight: 0 }} />
                                        </Tooltip>
                                        : <>
                                            <Icon name={t.icon} style={{ float: "left", marginRight: 10 }} />
                                            <span className="icon-only-title">{t.title}</span>
                                        </>
                                    : <>
                                        {t.icon && <Icon name={t.icon} style={{ float: "left", marginRight: 10 }} />}
                                        {t.title}
                                    </>
                                }
                                {t.active && <T.Underline hasIcon={t.icon !== undefined} direction={direction} checked={t.checked} hasMsg={!!t.msg} />}
                                {(t.checked === true && !iconOnly) && <Icon name={"check"} color={"@accentGreen"} style={{ float: "right", marginLeft: 10 }} />}
                                {(t.msg && !iconOnly) && <Tooltip noMargin tooltipText={t.msg} position='top'>
                                    <Icon name={"asterisk"} color={"@accentRed"} style={{ float: "right", marginLeft: 10 }} />
                                </Tooltip>}
                            </T.Tab>
                        );
                    })}
                    <T.Rest>{additionalContent}</T.Rest>
                </>
            )}
        </T.Container>
    );
}
export default TabsBar;
